import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { Modal } from 'react-bootstrap';
import { Blocks } from 'react-loader-spinner';
import { saveAs } from 'file-saver';
import MyPage from '../../MyPage';
import SubTabsInputs from '../../SubTabsInputs';
import { isSuperAdmin } from '../../helpers/user';
import axiosService from '../../helpers/axios';

const PAGE_SIZE = 100;

// [stateKey, apiParam, label] for the server-side filter bar.
const FILTERS = [
  ['year', 'year', 'Fiscal Year'],
  ['region', 'region', 'Region'],
  ['warehouse', 'warehouse', 'Warehouse'],
  ['lot', 'lot', 'Lot'],
  ['subtask', 'subtask', 'SubTask'],
  ['uom', 'uom', 'UOM'],
  ['country', 'country', 'Country'],
];

// [field, shortLabel] for the 12 month columns / form inputs.
const MONTHS = [
  ['july', 'Jul'], ['august', 'Aug'], ['september', 'Sep'], ['october', 'Oct'],
  ['november', 'Nov'], ['december', 'Dec'], ['january', 'Jan'], ['february', 'Feb'],
  ['march', 'Mar'], ['april', 'Apr'], ['may', 'May'], ['june', 'Jun'],
];

const EMPTY_FORM = {
  recordid: 0,
  fyname: '',
  warehousename: '',
  costgridlotname: '',
  costgridsubtaskname: '',
  uomname: '',
  countryname: '',
  devisename: '',
  ...Object.fromEntries(MONTHS.map(([f]) => [f, ''])),
};

const formatNumber = (value) => {
  if (value == null || value === '') return '';
  const n = Number(value);
  if (Number.isNaN(n)) return value;
  return new Intl.NumberFormat('de-DE', { maximumFractionDigits: 2 }).format(n);
};

const unique = (arr) => Array.from(new Set(arr.filter((v) => v != null && v !== '')));

export default function InputsCostGrid() {
  const canEdit = isSuperAdmin();

  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [nextPageUrl, setNextPageUrl] = useState(null);
  const [previousPageUrl, setPreviousPageUrl] = useState(null);
  const [filters, setFilters] = useState(Object.fromEntries(FILTERS.map(([k]) => [k, ''])));
  const [quickFilterText, setQuickFilterText] = useState('');

  // Reference data for the form dropdowns.
  const [fiscalYears, setFiscalYears] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [countries, setCountries] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [costgridFilter, setCostgridFilter] = useState([]); // rows: { lotnamefilter, subtasknamefilter, uomnamefilter }

  // Modal / form state.
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [isEdit, setIsEdit] = useState(false);
  const [file, setFile] = useState(null);

  const buildFilterParams = useCallback(() => {
    const params = { page_size: PAGE_SIZE };
    FILTERS.forEach(([key, apiParam]) => {
      const value = filters[key]?.trim();
      if (value) params[apiParam] = value;
    });
    return params;
  }, [filters]);

  const fetchPage = useCallback(async (url = null) => {
    setLoading(true);
    try {
      const res = url
        ? await axiosService.get(url)
        : await axiosService.get('/costgrid/', { params: buildFilterParams() });
      const data = res.data;
      const results = data.results ?? data ?? [];
      setRowData(Array.isArray(results) ? results : []);
      setNextPageUrl(data.next || null);
      setPreviousPageUrl(data.previous || null);
      setTotalPages(data.count ? Math.ceil(data.count / PAGE_SIZE) : 1);
      if (!url) setCurrentPage(1);
    } catch (err) {
      console.error('Error fetching costgrid:', err);
      setRowData([]);
    } finally {
      setLoading(false);
    }
  }, [buildFilterParams]);

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  const handleNextPage = () => {
    if (nextPageUrl) {
      setCurrentPage((page) => page + 1);
      fetchPage(nextPageUrl);
    }
  };

  const handlePreviousPage = () => {
    if (previousPageUrl) {
      setCurrentPage((page) => page - 1);
      fetchPage(previousPageUrl);
    }
  };

  // Load reference data once.
  useEffect(() => {
    const toArr = (d) => (Array.isArray(d) ? d : (d?.results ?? []));
    const activeNames = (data, name) => toArr(data).filter((d) => d.isactive).map((d) => d[name]);
    axiosService.get('/years/').then((r) => setFiscalYears(activeNames(r.data, 'fyname'))).catch(() => {});
    axiosService.get('/warehouses/').then((r) => setWarehouses(activeNames(r.data, 'warehousename'))).catch(() => {});
    axiosService.get('/countries/').then((r) => setCountries(activeNames(r.data, 'countryname'))).catch(() => {});
    axiosService.get('/currencies/').then((r) => setCurrencies(toArr(r.data).map((d) => d.devisename))).catch(() => {});
    axiosService.get('/costgridfilter/').then((r) => setCostgridFilter(toArr(r.data))).catch(() => {});
  }, []);

  // Cascading Lot -> SubTask -> UOM options, derived from /costgridfilter/.
  const lotOptions = useMemo(() => unique(costgridFilter.map((r) => r.lotnamefilter)), [costgridFilter]);
  const subtaskOptions = useMemo(
    () => unique(costgridFilter.filter((r) => r.lotnamefilter === form.costgridlotname).map((r) => r.subtasknamefilter)),
    [costgridFilter, form.costgridlotname]
  );
  const uomOptions = useMemo(
    () => unique(
      costgridFilter
        .filter((r) => r.lotnamefilter === form.costgridlotname && r.subtasknamefilter === form.costgridsubtaskname)
        .map((r) => r.uomnamefilter)
    ),
    [costgridFilter, form.costgridlotname, form.costgridsubtaskname]
  );

  const columnDefs = useMemo(() => {
    const cols = [
      { headerName: 'Fiscal Year', width: 110, valueGetter: (p) => p.data?.year?.fyname },
      { headerName: 'Region', width: 130, valueGetter: (p) => p.data?.warehouseCountryRegion?.warehousecountry?.region?.regionname },
      { headerName: 'Warehouse', width: 150, valueGetter: (p) => p.data?.warehouse?.warehousename },
      { headerName: 'Lot', width: 150, valueGetter: (p) => p.data?.lot?.costgridlotname },
      { headerName: 'SubTask', width: 170, valueGetter: (p) => p.data?.subtask?.costgridsubtaskname },
      { headerName: 'UOM', width: 130, valueGetter: (p) => p.data?.uom?.uomname },
      { headerName: 'Country', width: 130, valueGetter: (p) => p.data?.country?.countryname },
      { headerName: 'Currency', width: 100, valueGetter: (p) => p.data?.currency?.devise },
      ...MONTHS.map(([field, label]) => ({
        field,
        headerName: label,
        width: 90,
        filter: false,
        type: 'rightAligned',
        valueFormatter: (p) => formatNumber(p.value),
      })),
    ];
    if (canEdit) {
      cols.push({
        headerName: 'Options',
        width: 150,
        pinned: 'right',
        filter: false,
        sortable: false,
        cellRenderer: OptionsCell,
        cellRendererParams: {
          onEdit: openEdit,
          onCopy: openCopy,
          onDelete: handleDelete,
        },
      });
    }
    return cols;
    // openEdit/openCopy/handleDelete are stable for the component's lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canEdit]);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    resizable: true,
    filter: true,
    floatingFilter: true,
  }), []);

  const setFilter = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));
  const clearFilters = () => setFilters(Object.fromEntries(FILTERS.map(([k]) => [k, ''])));
  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  function openAdd() {
    setForm(EMPTY_FORM);
    setIsEdit(false);
    setShowModal(true);
  }

  function fillForm(dep) {
    return {
      recordid: dep.recordid,
      fyname: dep.year?.fyname || '',
      warehousename: dep.warehouse?.warehousename || '',
      costgridlotname: dep.lot?.costgridlotname || '',
      costgridsubtaskname: dep.subtask?.costgridsubtaskname || '',
      uomname: dep.uom?.uomname || '',
      countryname: dep.country?.countryname || '',
      devisename: dep.currency?.devisename || '',
      ...Object.fromEntries(MONTHS.map(([f]) => [f, dep[f] ?? ''])),
    };
  }

  function openEdit(dep) {
    setForm(fillForm(dep));
    setIsEdit(true);
    setShowModal(true);
  }

  function openCopy(dep) {
    setForm({ ...fillForm(dep), recordid: 0 });
    setIsEdit(false);
    setShowModal(true);
  }

  function validateForm() {
    const required = [
      ['fyname', 'Year'], ['warehousename', 'Warehouse'], ['countryname', 'Country'],
      ['costgridlotname', 'Lot'], ['costgridsubtaskname', 'SubTask'],
      ['uomname', 'UOM'], ['devisename', 'Currency'],
    ];
    for (const [key, label] of required) {
      if (!form[key]) {
        alert(`${label} is required`);
        return false;
      }
    }
    return true;
  }

  function buildPayload() {
    const payload = {
      countryname: form.countryname,
      warehousename: form.warehousename,
      fyname: form.fyname,
      costgridlotname: form.costgridlotname,
      costgridsubtaskname: form.costgridsubtaskname,
      uomname: form.uomname,
      devisename: form.devisename,
    };
    MONTHS.forEach(([f]) => { payload[f] = form[f]; });
    return payload;
  }

  function handleSave() {
    if (!validateForm()) return;
    const request = isEdit
      ? axiosService.patch(`/costgrid/${form.recordid}/`, buildPayload())
      : axiosService.post('/costgridcreate/', buildPayload());
    request
      .then(() => {
        alert(isEdit ? 'Update successful' : 'CostGrid created');
        setShowModal(false);
        fetchPage();
      })
      .catch((err) => alert(err.response?.data?.detail || 'Failed'));
  }

  function handleDelete(dep) {
    if (!window.confirm('Are you sure?')) return;
    axiosService
      .delete(`/costgrid/${dep.recordid}/`)
      .then(() => {
        alert('Delete successful');
        fetchPage();
      })
      .catch((err) => alert(err.message));
  }

  function handleUpload() {
    if (!file) {
      alert('Please select a file to upload.');
      return;
    }
    const formData = new FormData();
    formData.append('file', file);
    axiosService
      .post('/costgridupload/', formData, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then(() => {
        alert('File uploaded successfully.');
        fetchPage();
      })
      .catch((error) => {
        const errors = error.response?.data?.errors;
        const detail = errors?.length
          ? `\n\nErrors:\n${errors.map((e) => `Row ${e.row}: ${e.error}`).join('\n')}`
          : '';
        alert(`Upload failed${detail}`);
      });
  }

  async function downloadCSV() {
    const headers = [
      'fiscalyear', 'warehouse', 'lot', 'subtask', 'uom', 'country', 'devise',
      ...MONTHS.map(([f]) => f),
    ];
    const escape = (field) => {
      if (field == null) return 'N/A';
      return typeof field === 'string' ? `"${field.replace(/"/g, '""')}"` : field;
    };
    let allRows = [];
    try {
      let res = await axiosService.get('/costgrid/', { params: buildFilterParams() });
      while (res) {
        const data = res.data;
        allRows.push(...(data.results || data || []));
        res = data.next ? await axiosService.get(data.next) : null;
      }
    } catch (err) {
      console.error('Error fetching costgrid for export:', err);
      alert('Failed to download data');
      return;
    }
    const rows = allRows.map((dep) => [
      escape(dep.year?.fyname),
      escape(dep.warehouse?.warehousename),
      escape(dep.lot?.costgridlotname),
      escape(dep.subtask?.costgridsubtaskname),
      escape(dep.uom?.uomname),
      escape(dep.country?.countryname),
      escape(dep.currency?.devise),
      ...MONTHS.map(([f]) => escape(dep[f])),
    ]);
    const csv = [headers, ...rows].map((r) => r.join(';')).join('\r\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
    saveAs(blob, 'costgrid.csv');
  }

  return (
    <div className="scrollable-container">
      <MyPage />
      <SubTabsInputs />

      <div className="d-flex flex-wrap align-items-center gap-2 m-2">
        {canEdit && (
          <button type="button" className="btn btn-success btn-sm" onClick={openAdd}>
            Add CostGrid
          </button>
        )}
        <input
          className="form-control form-control-sm"
          style={{ width: '220px' }}
          placeholder="Search in grid..."
          value={quickFilterText}
          onChange={(e) => setQuickFilterText(e.target.value)}
        />
        <div className="ms-auto d-flex align-items-center gap-2">
          {canEdit && (
            <>
              <input
                type="file"
                className="form-control form-control-sm"
                style={{ width: '220px' }}
                onChange={(e) => setFile(e.target.files[0])}
              />
              <button type="button" className="btn btn-primary btn-sm" onClick={handleUpload}>
                Upload File
              </button>
            </>
          )}
          <button type="button" className="btn btn-primary btn-sm" onClick={downloadCSV}>
            Download
          </button>
        </div>
      </div>

      <div className="d-flex flex-wrap align-items-end gap-2 m-2">
        {FILTERS.map(([key, , label]) => (
          <div key={key}>
            <label className="form-label mb-0" style={{ fontSize: '11px' }}>{label}</label>
            <input
              className="form-control form-control-sm"
              style={{ width: '130px' }}
              placeholder="Filter"
              value={filters[key]}
              onChange={(e) => setFilter(key, e.target.value)}
            />
          </div>
        ))}
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={clearFilters}>
          Clear filters
        </button>
      </div>

      {loading ? (
        <div className="d-flex flex-column justify-content-center align-items-center" style={{ height: '40vh' }}>
          <Blocks visible height="100" width="100" ariaLabel="loading" />
          <div>Loading...</div>
        </div>
      ) : (
        <div className="ag-theme-quartz" style={{ height: '70vh', width: '100%' }}>
          <AgGridReact
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            quickFilterText={quickFilterText}
            animateRows
            getRowId={(p) => String(p.data?.recordid ?? '')}
          />
        </div>
      )}

      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '10px',
        padding: '20px',
        fontSize: '12px',
      }}
      >
        <button
          type="button"
          onClick={handlePreviousPage}
          disabled={!previousPageUrl || loading}
          className="btn btn-primary"
          style={{ height: '28px', fontSize: '12px' }}
        >
          Previous
        </button>

        <span style={{ fontSize: '12px' }}>
          Page {currentPage} of {totalPages}
        </span>

        <button
          type="button"
          onClick={handleNextPage}
          disabled={!nextPageUrl || loading}
          className="btn btn-primary"
          style={{ height: '28px', fontSize: '12px' }}
        >
          Next
        </button>
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '14px' }}>
            {isEdit ? 'Edit CostGrid' : 'Add CostGrid'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <FormSelect label="Fiscal Year" value={form.fyname} disabled={isEdit}
            options={fiscalYears} onChange={(v) => setField('fyname', v)} />
          <FormSelect label="Warehouse" value={form.warehousename} disabled={isEdit}
            options={warehouses} onChange={(v) => setField('warehousename', v)} />
          <FormSelect label="Lot" value={form.costgridlotname} options={lotOptions}
            onChange={(v) => setForm((prev) => ({ ...prev, costgridlotname: v, costgridsubtaskname: '', uomname: '' }))} />
          <FormSelect label="SubTask" value={form.costgridsubtaskname} options={subtaskOptions}
            onChange={(v) => setForm((prev) => ({ ...prev, costgridsubtaskname: v, uomname: '' }))} />
          <FormSelect label="UOM" value={form.uomname} options={uomOptions}
            onChange={(v) => setField('uomname', v)} />
          <FormSelect label="Country" value={form.countryname} options={countries}
            onChange={(v) => setField('countryname', v)} />
          <FormSelect label="Currency" value={form.devisename} options={currencies}
            onChange={(v) => setField('devisename', v)} />

          {MONTHS.map(([field, label]) => (
            <div className="input-group input-group-sm mb-2" key={field}>
              <span className="input-group-text" style={{ width: '60px' }}>{label}</span>
              <input
                type="number"
                className="form-control"
                value={form[field]}
                onChange={(e) => setField(field, e.target.value)}
              />
            </div>
          ))}
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowModal(false)}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary btn-sm" onClick={handleSave}>
            {isEdit ? 'Update' : 'Create'}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

function FormSelect({ label, value, options, onChange, disabled }) {
  return (
    <div className="input-group input-group-sm mb-2">
      <span className="input-group-text" style={{ width: '110px' }}>{label}</span>
      <select
        className="form-select"
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">Select {label.toLowerCase()}</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
    </div>
  );
}

function OptionsCell({ data, onEdit, onCopy, onDelete }) {
  return (
    <div className="d-flex align-items-center gap-2" style={{ height: '100%' }}>
      <button type="button" className="btn btn-light btn-sm p-1" title="Edit" onClick={() => onEdit(data)}>
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
          <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z" />
          <path fillRule="evenodd" d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5v11z" />
        </svg>
      </button>
      <button type="button" className="btn btn-light btn-sm p-1" title="Delete" onClick={() => onDelete(data)}>
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
          <path d="M2.5 1a1 1 0 0 0-1 1v1a1 1 0 0 0 1 1H3v9a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V4h.5a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H10a1 1 0 0 0-1-1H7a1 1 0 0 0-1 1H2.5zm3 4a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-1 0v-7a.5.5 0 0 1 .5-.5zM8 5a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-1 0v-7A.5.5 0 0 1 8 5zm3 .5v7a.5.5 0 0 1-1 0v-7a.5.5 0 0 1 1 0z" />
        </svg>
      </button>
      <button type="button" className="btn btn-primary btn-sm py-0" onClick={() => onCopy(data)}>
        Copy
      </button>
    </div>
  );
}
